/**
 * Authentication and tenant-related errors
 */

export class AuthError extends Error {
  code?: string

  constructor(message: string, code?: string) {
    super(message)
    this.name = 'AuthError'
    this.code = code
  }
}

export class TenantError extends Error {
  code?: string

  constructor(message: string, code?: string) {
    super(message)
    this.name = 'TenantError'
    this.code = code
  }
}

export class EnvironmentError extends Error {
  code?: string

  constructor(message: string, code?: string) {
    super(message)
    this.name = 'EnvironmentError'
    this.code = code
  }
}

export class NetworkError extends Error {
  statusCode?: number

  constructor(message: string, statusCode?: number) {
    super(message)
    this.name = 'NetworkError'
    this.statusCode = statusCode
  }
}

export const AUTH_ERROR_CODES = {
  UNAUTHORIZED: 'UNAUTHORIZED',
  TOKEN_EXPIRED: 'TOKEN_EXPIRED',
  INVALID_CREDENTIALS: 'INVALID_CREDENTIALS',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
} as const

export const TENANT_ERROR_CODES = {
  NO_TENANTS: 'NO_TENANTS',
  TENANT_NOT_FOUND: 'TENANT_NOT_FOUND',
  ACCESS_DENIED: 'ACCESS_DENIED',
  SWITCH_FAILED: 'SWITCH_FAILED',
} as const

export const ENVIRONMENT_ERROR_CODES = {
  NO_ENVIRONMENTS: 'NO_ENVIRONMENTS',
  ENVIRONMENT_NOT_FOUND: 'ENVIRONMENT_NOT_FOUND',
  INVALID_ENVIRONMENT: 'INVALID_ENVIRONMENT',
} as const

/**
 * Parse error from API response or unknown error
 */
export function parseError(error: unknown): {
  message: string
  code?: string
  statusCode?: number
} {
  if (
    error instanceof AuthError ||
    error instanceof TenantError ||
    error instanceof EnvironmentError
  ) {
    return {
      message: error.message,
      code: error.code,
    }
  }

  if (error instanceof NetworkError) {
    return {
      message: error.message,
      statusCode: error.statusCode,
    }
  }

  if (error instanceof Error) {
    return {
      message: error.message,
    }
  }

  return {
    message: 'An unexpected error occurred',
  }
}

/**
 * Check if error is a network/connectivity issue
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof NetworkError) return true
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return (
      message.includes('network') ||
      message.includes('fetch') ||
      message.includes('connection')
    )
  }
  return false
}

/**
 * Check if error requires user to re-authenticate
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof AuthError) return true
  if (error instanceof NetworkError) {
    return error.statusCode === 401 || error.statusCode === 403
  }
  if (error instanceof Error) {
    const message = error.message.toLowerCase()
    return (
      message.includes('unauthorized') ||
      message.includes('forbidden') ||
      message.includes('expired') ||
      message.includes('invalid token')
    )
  }
  return false
}
